/**
 * Category Budget Limits Page
 * 
 * Page for managing category-specific budget alerts.
 * Features:
 * - View and manage category budget limits
 * - Create new category limits with warning thresholds
 * - View real-time spending status by category
 * - View alert history
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/auth';
import {
  categoryBudgetAlertService,
  type CategoryBudgetLimit,
  type CategorySpendingStatus,
  type CategoryAlertHistoryItem,
} from '../api';
import {
  Button,
  Card,
  Badge,
  PageHeader,
  PageLoading,
  NoOrganizationMessage,
  Modal,
  Input,
  Table,
  formatEuro,
  formatDateTime,
} from '../components';

// Alert level display mapping
const alertLevelLabels: Record<string, string> = {
  normal: 'Normaali',
  info: 'Info',
  warning: 'Varoitus',
  critical: 'Kriittinen',
};

const alertLevelColors: Record<string, string> = {
  normal: 'bg-green-100 text-green-800',
  info: 'bg-blue-100 text-blue-800',
  warning: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

// Progress bar color based on alert level
function getProgressBarColor(level: string): string {
  switch (level) {
    case 'critical': return 'bg-red-600';
    case 'warning': return 'bg-orange-500';
    case 'info': return 'bg-blue-500';
    default: return 'bg-green-500';
  }
}

export default function CategoryBudgetLimitsPage() {
  const { t } = useTranslation();
  const { activeOrg } = useAuthStore();
  const [limits, setLimits] = useState<CategoryBudgetLimit[]>([]);
  const [statuses, setStatuses] = useState<CategorySpendingStatus[]>([]);
  const [history, setHistory] = useState<CategoryAlertHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'limits' | 'status' | 'history'>('status');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingLimit, setEditingLimit] = useState<CategoryBudgetLimit | null>(null);

  useEffect(() => {
    if (activeOrg) {
      loadData();
    }
  }, [activeOrg, activeTab, selectedYear]);

  const loadData = async () => {
    if (!activeOrg) return;
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'limits') {
        const limitsData = await categoryBudgetAlertService.listLimits(activeOrg.id, selectedYear);
        setLimits(limitsData);
      } else if (activeTab === 'status') {
        const statusData = await categoryBudgetAlertService.getSpendingStatus(activeOrg.id, selectedYear);
        setStatuses(statusData);
      } else if (activeTab === 'history') {
        const historyData = await categoryBudgetAlertService.getHistory(activeOrg.id, { 
          fiscalYear: selectedYear,
          limit: 50 
        });
        setHistory(historyData.history);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLimit = async (data: {
    category: string;
    limit_amount?: number;
    warning_percent: number;
    critical_percent: number;
    exceeded_percent: number;
    email_enabled: boolean;
    notification_enabled: boolean;
  }) => {
    if (!activeOrg) return;
    try {
      await categoryBudgetAlertService.createLimit({
        ...data,
        organization_id: activeOrg.id,
        fiscal_year: selectedYear,
      });
      await loadData();
      setShowCreateModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create limit');
    }
  };

  const handleUpdateLimit = async (limitId: string, updates: {
    limit_amount?: number | null;
    warning_percent?: number;
    critical_percent?: number;
    exceeded_percent?: number;
    email_enabled?: boolean;
    notification_enabled?: boolean;
    is_active?: boolean;
  }) => {
    if (!activeOrg) return;
    try {
      await categoryBudgetAlertService.updateLimit(limitId, {
        ...updates,
        organization_id: activeOrg.id,
      });
      await loadData();
      setEditingLimit(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update limit');
    }
  };

  const handleDeleteLimit = async (limitId: string) => {
    if (!activeOrg) return;
    if (!confirm(t('budget.deleteLimitConfirm'))) return;
    try {
      await categoryBudgetAlertService.deleteLimit(limitId, activeOrg.id);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete limit');
    }
  };

  const handleTriggerCheck = async () => {
    if (!activeOrg) return;
    try {
      const result = await categoryBudgetAlertService.triggerCheck(activeOrg.id, {
        fiscal_year: selectedYear,
      });
      alert(`Tarkistus valmis: ${result.triggered} hälytystä laukaistu`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger check');
    }
  };

  // Get unique categories from transactions (for create modal)
  const getAvailableCategories = (): string[] => {
    const categoriesFromStatus = statuses.map(s => s.category);
    const categoriesFromLimits = limits.map(l => l.category);
    return [...new Set([...categoriesFromStatus, ...categoriesFromLimits])].sort();
  };

  if (!activeOrg) {
    return <NoOrganizationMessage />;
  }

  if (loading) return <PageLoading message="Ladataan kategoriabudjetteja..." />;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        title={`📊 Kategoriabudjetit — ${activeOrg.name}`}
        action={
          <div className="flex gap-2">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              {[selectedYear - 1, selectedYear, selectedYear + 1].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <Button onClick={handleTriggerCheck} variant="secondary">
              🔄 Tarkista nyt
            </Button>
            <Button onClick={() => setShowCreateModal(true)} variant="primary">
              ➕ Uusi raja
            </Button>
          </div>
        }
      />

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {[
          { id: 'status', label: '📊 Tilanne' },
          { id: 'limits', label: '⚙️ Rajat' },
          { id: 'history', label: '📜 Historia' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Status Tab */}
      {activeTab === 'status' && (
        <div className="space-y-4">
          {statuses.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <div className="text-4xl mb-4">📊</div>
              <p className="text-lg font-medium">Ei kategoriadataa</p>
              <p className="text-sm">Ei tapahtumia tai rajoituksia valitulla kaudella.</p>
              <Button onClick={() => setShowCreateModal(true)} variant="primary" className="mt-4">
                ➕ Luo ensimmäinen kategoriaraja
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {statuses.map((status) => (
                <Card key={status.category} className="flex flex-col">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-semibold text-gray-900 text-lg">{status.category}</h3>
                    <Badge className={alertLevelColors[status.alertLevel]}>
                      {alertLevelLabels[status.alertLevel]}
                    </Badge>
                  </div>

                  {status.limitAmount !== null && (
                    <>
                      <div className="mb-4">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">
                            {formatEuro(status.totalSpent)} / {formatEuro(status.limitAmount)}
                          </span>
                          <span className="font-medium">{status.spentPercent.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div
                            className={`h-2.5 rounded-full ${getProgressBarColor(status.alertLevel)}`}
                            style={{ width: `${Math.min(status.spentPercent, 100)}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-gray-400 mt-1">
                          <span>{status.warningPercent}%</span>
                          <span>{status.criticalPercent}%</span>
                          <span>{status.exceededPercent}%</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-sm mb-4">
                        <div className="bg-gray-50 p-2 rounded">
                          <span className="text-gray-500">Jäljellä:</span>
                          <span className="font-medium ml-1">
                            {status.remainingAmount !== null ? formatEuro(status.remainingAmount) : '-'}
                          </span>
                        </div>
                        <div className="bg-gray-50 p-2 rounded">
                          <span className="text-gray-500">Tapahtumat:</span>
                          <span className="font-medium ml-1">{status.transactionCount}</span>
                        </div>
                      </div>
                    </>
                  )}

                  {status.limitAmount === null && (
                    <div className="py-4 text-center text-gray-500">
                      <p>Ei asetettua rajaa</p>
                      <Button 
                        onClick={() => setShowCreateModal(true)} 
                        variant="secondary" 
                        size="sm"
                        className="mt-2"
                      >
                        ➕ Aseta raja
                      </Button>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Limits Tab */}
      {activeTab === 'limits' && (
        <div className="space-y-4">
          {limits.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <div className="text-4xl mb-4">⚙️</div>
              <p className="text-lg font-medium">Ei kategoriarajoja</p>
              <p className="text-sm">Luo uusi kategoriaraja saadaksesi hälytykset.</p>
              <Button onClick={() => setShowCreateModal(true)} variant="primary" className="mt-4">
                ➕ Luo kategoriaraja
              </Button>
            </div>
          ) : (
            <Table
              columns={[
                { key: 'category', header: 'Kategoria', render: (item: CategoryBudgetLimit) => item.category },
                { key: 'limit', header: 'Raja', render: (item: CategoryBudgetLimit) => 
                  item.limitAmount !== null ? formatEuro(item.limitAmount) : '-' 
                },
                { key: 'thresholds', header: 'Hälytysrajat', render: (item: CategoryBudgetLimit) => (
                  <div className="text-sm">
                    <span className="text-blue-600">{item.warningPercent}%</span> / 
                    <span className="text-orange-600">{item.criticalPercent}%</span> / 
                    <span className="text-red-600">{item.exceededPercent}%</span>
                  </div>
                )},
                { key: 'notifications', header: 'Ilmoitukset', render: (item: CategoryBudgetLimit) => (
                  <div className="flex gap-2">
                    {item.emailEnabled && <span title="Sähköposti">📧</span>}
                    {item.notificationEnabled && <span title="Sovellus">🔔</span>}
                  </div>
                )},
                { key: 'status', header: 'Tila', render: (item: CategoryBudgetLimit) => (
                  <Badge className={item.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                    {item.isActive ? 'Aktiivinen' : 'Poissa käytöstä'}
                  </Badge>
                )},
                { key: 'actions', header: '', render: (item: CategoryBudgetLimit) => (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setEditingLimit(item)}
                      variant="secondary"
                      size="sm"
                    >
                      ✏️
                    </Button>
                    <Button
                      onClick={() => handleDeleteLimit(item.id)}
                      variant="danger"
                      size="sm"
                    >
                      🗑️
                    </Button>
                  </div>
                )},
              ]}
              data={limits}
              keyExtractor={(item) => item.id}
              emptyMessage="Ei kategoriarajoja"
            />
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <Table
          columns={[
            { key: 'created_at', header: 'Aika', render: (item: CategoryAlertHistoryItem) => formatDateTime(item.createdAt) },
            { key: 'category', header: 'Kategoria', render: (item: CategoryAlertHistoryItem) => item.category },
            { key: 'level', header: 'Taso', render: (item: CategoryAlertHistoryItem) => (
              <Badge className={alertLevelColors[item.alertLevel] || 'bg-gray-100'}>
                {alertLevelLabels[item.alertLevel] || item.alertLevel}
              </Badge>
            )},
            { key: 'message', header: 'Viesti', render: (item: CategoryAlertHistoryItem) => (
              <span className="text-sm text-gray-600 truncate max-w-xs block">{item.message}</span>
            )},
            { key: 'status', header: 'Status', render: (item: CategoryAlertHistoryItem) => (
              <Badge
                variant={
                  item.status === 'delivered' ? 'success' : 
                  item.status === 'failed' ? 'error' : 
                  'neutral'
                }
              >
                {item.status === 'delivered' ? 'Toimitettu' : 
                 item.status === 'failed' ? 'Epäonnistui' : 
                 'Lähetetty'}
              </Badge>
            )},
          ]}
          data={history}
          keyExtractor={(item) => item.id}
          emptyMessage="Ei hälytyshistoriaa"
        />
      )}

      {/* Create Limit Modal */}
      {showCreateModal && (
        <CreateLimitModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateLimit}
          availableCategories={getAvailableCategories()}
        />
      )}

      {/* Edit Limit Modal */}
      {editingLimit && (
        <EditLimitModal
          limit={editingLimit}
          onClose={() => setEditingLimit(null)}
          onSave={(updates) => handleUpdateLimit(editingLimit.id, updates)}
        />
      )}
    </div>
  );
}

// Create Limit Modal Component
interface CreateLimitModalProps {
  onClose: () => void;
  onSave: (data: {
    category: string;
    limit_amount?: number;
    warning_percent: number;
    critical_percent: number;
    exceeded_percent: number;
    email_enabled: boolean;
    notification_enabled: boolean;
  }) => void;
  availableCategories: string[];
}

function CreateLimitModal({ onClose, onSave, availableCategories }: CreateLimitModalProps) {
  const [formData, setFormData] = useState({
    category: '',
    customCategory: '',
    limit_amount: '',
    warning_percent: 75,
    critical_percent: 90,
    exceeded_percent: 100,
    email_enabled: true,
    notification_enabled: true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const category = formData.category === '__custom__' ? formData.customCategory : formData.category;
    if (!category) return;
    
    onSave({
      category,
      limit_amount: formData.limit_amount ? Math.round(parseFloat(formData.limit_amount) * 100) : undefined,
      warning_percent: formData.warning_percent,
      critical_percent: formData.critical_percent,
      exceeded_percent: formData.exceeded_percent,
      email_enabled: formData.email_enabled,
      notification_enabled: formData.notification_enabled,
    });
  };

  return (
    <Modal onClose={onClose} title="➕ Uusi kategoriaraja">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Kategoria *</label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          >
            <option value="">Valitse kategoria...</option>
            {availableCategories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
            <option value="__custom__">+ Uusi kategoria...</option>
          </select>
        </div>

        {formData.category === '__custom__' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Uusi kategoria *</label>
            <Input
              type="text"
              value={formData.customCategory}
              onChange={(e) => setFormData({ ...formData, customCategory: e.target.value })}
              placeholder="Esim. Matkakulut"
              required
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Rahamääräraja (€) - valinnainen
          </label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={formData.limit_amount}
            onChange={(e) => setFormData({ ...formData, limit_amount: e.target.value })}
            placeholder="Esim. 500"
          />
          <p className="text-xs text-gray-500 mt-1">
            Jos et aseta rahamäärää, hälytykset perustuvat prosentteihin
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Info (%)</label>
            <Input
              type="number"
              min="0"
              max="100"
              value={formData.warning_percent}
              onChange={(e) => setFormData({ ...formData, warning_percent: parseInt(e.target.value) })}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Varoitus (%)</label>
            <Input
              type="number"
              min="0"
              max="100"
              value={formData.critical_percent}
              onChange={(e) => setFormData({ ...formData, critical_percent: parseInt(e.target.value) })}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Kriittinen (%)</label>
            <Input
              type="number"
              min="0"
              max="100"
              value={formData.exceeded_percent}
              onChange={(e) => setFormData({ ...formData, exceeded_percent: parseInt(e.target.value) })}
            />
          </div>
        </div>

        <div className="flex gap-4 pt-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.email_enabled}
              onChange={(e) => setFormData({ ...formData, email_enabled: e.target.checked })}
              className="mr-2"
            />
            <span className="text-sm">Sähköposti</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.notification_enabled}
              onChange={(e) => setFormData({ ...formData, notification_enabled: e.target.checked })}
              className="mr-2"
            />
            <span className="text-sm">Sovellusilmoitus</span>
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Peruuta
          </Button>
          <Button type="submit" variant="primary">
            💾 Luo raja
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Edit Limit Modal Component
interface EditLimitModalProps {
  limit: CategoryBudgetLimit;
  onClose: () => void;
  onSave: (updates: {
    limit_amount?: number | null;
    warning_percent?: number;
    critical_percent?: number;
    exceeded_percent?: number;
    email_enabled?: boolean;
    notification_enabled?: boolean;
    is_active?: boolean;
  }) => void;
}

function EditLimitModal({ limit, onClose, onSave }: EditLimitModalProps) {
  const [formData, setFormData] = useState({
    limit_amount: limit.limitAmount !== null ? (limit.limitAmount / 100).toFixed(2) : '',
    warning_percent: limit.warningPercent,
    critical_percent: limit.criticalPercent,
    exceeded_percent: limit.exceededPercent,
    email_enabled: limit.emailEnabled,
    notification_enabled: limit.notificationEnabled,
    is_active: limit.isActive,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      limit_amount: formData.limit_amount ? Math.round(parseFloat(formData.limit_amount) * 100) : null,
      warning_percent: formData.warning_percent,
      critical_percent: formData.critical_percent,
      exceeded_percent: formData.exceeded_percent,
      email_enabled: formData.email_enabled,
      notification_enabled: formData.notification_enabled,
      is_active: formData.is_active,
    });
  };

  return (
    <Modal onClose={onClose} title={`✏️ Muokkaa: ${limit.category}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Rahamääräraja (€)
          </label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={formData.limit_amount}
            onChange={(e) => setFormData({ ...formData, limit_amount: e.target.value })}
            placeholder="Ei rajaa"
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Info (%)</label>
            <Input
              type="number"
              min="0"
              max="100"
              value={formData.warning_percent}
              onChange={(e) => setFormData({ ...formData, warning_percent: parseInt(e.target.value) })}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Varoitus (%)</label>
            <Input
              type="number"
              min="0"
              max="100"
              value={formData.critical_percent}
              onChange={(e) => setFormData({ ...formData, critical_percent: parseInt(e.target.value) })}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Kriittinen (%)</label>
            <Input
              type="number"
              min="0"
              max="100"
              value={formData.exceeded_percent}
              onChange={(e) => setFormData({ ...formData, exceeded_percent: parseInt(e.target.value) })}
            />
          </div>
        </div>

        <div className="flex gap-4 pt-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.email_enabled}
              onChange={(e) => setFormData({ ...formData, email_enabled: e.target.checked })}
              className="mr-2"
            />
            <span className="text-sm">Sähköposti</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.notification_enabled}
              onChange={(e) => setFormData({ ...formData, notification_enabled: e.target.checked })}
              className="mr-2"
            />
            <span className="text-sm">Sovellusilmoitus</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="mr-2"
            />
            <span className="text-sm">Aktiivinen</span>
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Peruuta
          </Button>
          <Button type="submit" variant="primary">
            💾 Tallenna
          </Button>
        </div>
      </form>
    </Modal>
  );
}
